import React, { useEffect, useRef, useState } from 'react';
import Chart from 'chart.js/auto';
import adminServices from '../../services/AdminServices';

function RegistrationChart({ period = 'daily' }) {
    const [chartData, setChartData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const chartRef = useRef(null);
    const chartInstance = useRef(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);
                const res = await adminServices.registrationCharts(`?period=${period}`);
                setChartData(res.data);
            } catch (err) {
                console.error('Registration chart error:', err);
                setError('Failed to load registration metrics');
                setChartData(null);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [period]);

    useEffect(() => {
        if (
            !chartRef.current ||
            !Array.isArray(chartData) ||
            chartData.length === 0 ||
            loading
        ) {
            return;
        }

        const ctx = chartRef.current.getContext('2d');
        if (chartInstance.current) {
            chartInstance.current.destroy();
        }

        const labels = chartData.map((item) => item.date);
        const patientsData = chartData.map((item) => parseInt(item.patients, 10) || 0);
        const nursesData = chartData.map((item) => parseInt(item.nurses, 10) || 0);

        chartInstance.current = new Chart(ctx, {
            type: 'line',
            data: {
                labels,
                datasets: [
                    {
                        label: 'Patients',
                        data: patientsData,
                        borderColor: '#0ea5e9', // primary (sky-500)
                        backgroundColor: 'rgba(14, 165, 233, 0.1)',
                        tension: 0.3,
                        fill: true,
                        yAxisID: 'y',
                    },
                    {
                        label: 'Nurses',
                        data: nursesData,
                        borderColor: '#0d9488', // accent (teal-600)
                        backgroundColor: 'rgba(13, 148, 136, 0.1)',
                        tension: 0.3,
                        fill: true,
                        yAxisID: 'y',
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: { mode: 'index', intersect: false },
                scales: {
                    x: {
                        grid: { display: false },
                        ticks: { color: '#1e293b' }, // neutral (slate-800)
                    },
                    y: {
                        position: 'left',
                        title: { display: true, text: 'Registrations', color: '#1e293b' },
                        grid: { color: 'rgba(0,0,0,0.05)' },
                        ticks: { color: '#1e293b' },
                        beginAtZero: true,
                        min: 0,
                    },
                },
                plugins: {
                    title: {
                        display: true,
                        text: period === 'daily'
                            ? 'New Registrations (Last 30 Days)'
                            : 'New Registrations (Last 12 Months)',
                        color: '#1e293b',
                        font: { size: 16, weight: 'bold' },
                        padding: { top: 10, bottom: 15 }
                    },
                    legend: {
                        labels: {
                            color: '#1e293b',
                            usePointStyle: true,
                            padding: 10,
                        },
                    },
                    tooltip: {
                        mode: 'index',
                        intersect: false,
                        backgroundColor: '#1e293b',
                        titleColor: '#f1f5f9',
                        bodyColor: '#f1f5f9',
                        callbacks: {
                            label: function (context) {
                                let label = context.dataset.label || '';
                                if (label) label += ': ';
                                label += context.parsed.y;
                                return label;
                            },
                        },
                    },
                },
            },
        });
    }, [chartData, loading, period]);

    useEffect(() => {
        return () => {
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }
        };
    }, []);

    return (
        <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-border-light dark:border-border-dark">
            {loading ? (
                <div className="h-64 flex items-center justify-center text-gray-500">
                    Loading registration metrics...
                </div>
            ) : error ? (
                <div className="h-64 flex items-center justify-center text-error">
                    {error}
                </div>
            ) : chartData && chartData.length > 0 ? (
                <div className="h-72">
                    <canvas ref={chartRef} />
                </div>
            ) : (
                <div className="h-64 flex items-center justify-center text-gray-500">
                    No registration data available
                </div>
            )}
        </div>
    );
}

export default RegistrationChart;